import React, { useCallback, useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AgentAvatar from '../pages/AgentAvatar';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeEmailRecipientPicker — a single, reusable input that accepts three kinds
 * of recipients in one combined chip-style field:
 *   1. Account users — searched by name/email, rendered with an avatar.
 *   2. Free-form email addresses — typed by hand, validated on commit.
 *   3. Workflow placeholders (`${...}`) — picked from the dropdown when the
 *      user types `$`, or typed literally.
 *
 * Two output modes:
 *
 * SPLIT mode (default, for email recipients):
 *   Users go to `selectedUserIds`; free-form emails + placeholder tokens go
 *   to `selectedEmails`. The backend resolves userIds to email addresses
 *   server-side and unions them with the free-form list.
 *
 *     <NodeEmailRecipientPicker
 *       selectedUserIds={userIds} selectedEmails={emails}
 *       onUserIdsChange={setUserIds} onEmailsChange={setEmails}
 *     />
 *
 * FLAT mode (for task assignees):
 *   The caller passes a single `value` array of strings, where each entry is
 *   either a username, an email, or a `${placeholder}` token. The picker
 *   classifies each entry at render-time by cross-referencing `users`, and
 *   emits a flat array on change. Use this when the backend stores a single
 *   opaque list (e.g. `assigneeUserIds`).
 *
 *     <NodeEmailRecipientPicker
 *       value={["alice", "contractor@x.com", "${customer.email}"]}
 *       onChange={setAssignees}
 *       maxItems={1}
 *     />
 *
 * Other props:
 *   - allowFreeFormEmails (default true): when false, free-solo commits
 *     must look like `${placeholder}` — plain emails are rejected. Used
 *     when the caller wants a users + placeholders picker only.
 *   - maxItems: optional cap on total selections (e.g. 1 for single-user
 *     assignment). When hit, older selections are pushed out FIFO.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PLACEHOLDER_REGEX = /^\$\{.+\}$/;

function isPlaceholderToken(value) {
  return typeof value === 'string' && PLACEHOLDER_REGEX.test(value.trim());
}

function isAcceptableFreeSolo(value, { allowFreeFormEmails }) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (isPlaceholderToken(trimmed)) return true;
  return allowFreeFormEmails && EMAIL_REGEX.test(trimmed);
}

// availablePlaceholders can be a flat list of { key, type } or a nested
// [{ placeholders: [...] }] shape — normalize to a flat list and drop
// form-only placeholders (they're not meaningful in an email recipient field).
function flattenPlaceholders(availablePlaceholders) {
  if (!Array.isArray(availablePlaceholders)) return [];
  const nested = availablePlaceholders[0]?.placeholders;
  const list = Array.isArray(nested) ? nested : availablePlaceholders;
  return list.filter((p) => p && p.key && p.type !== 'form_placeholder');
}

const NodeEmailRecipientPicker = React.memo(function NodeEmailRecipientPicker({
  label,
  placeholder,
  users = [],
  usersLoading = false,
  // Split-mode props:
  selectedUserIds,
  selectedEmails,
  onUserIdsChange,
  onEmailsChange,
  // Flat-mode props:
  value: flatValue,
  onChange: onFlatChange,
  // Options:
  availablePlaceholders = [],
  allowFreeFormEmails = true,
  maxItems,
  palette,
  disabled = false,
  helperText,
  sx,
}) {
  const safeUsers = Array.isArray(users) ? users : [];
  const isFlatMode = Array.isArray(flatValue) || typeof onFlatChange === 'function';

  const userById = useMemo(() => {
    const map = new Map();
    for (const u of safeUsers) {
      if (u && u.userName) map.set(u.userName, u);
    }
    return map;
  }, [safeUsers]);

  // In flat mode the caller hands us a single list of strings — classify each
  // entry as a user (if it matches a known username), a placeholder token
  // (${...}), or an email/opaque string. Unknown entries fall back to email
  // chips so stale data is never silently dropped on save.
  const { safeUserIds, safeEmails } = useMemo(() => {
    if (!isFlatMode) {
      return {
        safeUserIds: Array.isArray(selectedUserIds) ? selectedUserIds : [],
        safeEmails: Array.isArray(selectedEmails) ? selectedEmails : [],
      };
    }
    const uids = [];
    const ems = [];
    for (const raw of Array.isArray(flatValue) ? flatValue : []) {
      if (typeof raw !== 'string' || !raw.trim()) continue;
      const trimmed = raw.trim();
      if (userById.has(trimmed)) {
        uids.push(trimmed);
      } else {
        ems.push(trimmed);
      }
    }
    return { safeUserIds: uids, safeEmails: ems };
  }, [isFlatMode, flatValue, selectedUserIds, selectedEmails, userById]);

  const effectivePlaceholder =
    placeholder ||
    (allowFreeFormEmails
      ? 'Search users, type an email, or $ for a placeholder...'
      : 'Search users or type $ for a placeholder...');

  const placeholderOptions = useMemo(
    () =>
      flattenPlaceholders(availablePlaceholders).map((p) => ({
        kind: 'placeholder',
        key: p.key,
        token: `\${${p.key}}`,
        type: p.type || 'text',
      })),
    [availablePlaceholders]
  );

  // Dropdown options: users first, then workflow placeholders. Free-form
  // email text is handled via freeSolo + Enter, not as an option here.
  const options = useMemo(
    () => [
      ...safeUsers.map((u) => ({
        kind: 'user',
        userName: u.userName,
        displayName: u.displayName || u.userName,
        email: u.email || '',
      })),
      ...placeholderOptions,
    ],
    [safeUsers, placeholderOptions]
  );

  // Current value: selected users first (with full user metadata for avatars),
  // then free-form email chips. Unknown userIds are preserved as display-name
  // fallbacks so stale data isn't silently dropped on save.
  const value = useMemo(() => {
    const items = [];
    for (const id of safeUserIds) {
      const u = userById.get(id);
      if (u) {
        items.push({
          kind: 'user',
          userName: id,
          displayName: u.displayName || u.userName,
          email: u.email || '',
        });
      } else {
        items.push({ kind: 'user', userName: id, displayName: id, email: '' });
      }
    }
    for (const email of safeEmails) {
      items.push({ kind: 'email', value: email });
    }
    return items;
  }, [safeUserIds, safeEmails, userById]);

  const handleChange = useCallback(
    (_event, nextItems) => {
      const nextUserIds = [];
      const nextEmails = [];
      const flat = [];
      for (const item of nextItems || []) {
        if (typeof item === 'string') {
          const trimmed = item.trim();
          if (isAcceptableFreeSolo(trimmed, { allowFreeFormEmails })) {
            nextEmails.push(trimmed);
            flat.push(trimmed);
          }
        } else if (item && item.kind === 'user' && item.userName) {
          nextUserIds.push(item.userName);
          flat.push(item.userName);
        } else if (item && item.kind === 'placeholder' && item.token) {
          // Picking a placeholder from the dropdown stores its token alongside
          // free-form entries — the backend resolves it at send time.
          nextEmails.push(item.token);
          flat.push(item.token);
        } else if (item && item.kind === 'email' && item.value) {
          nextEmails.push(item.value);
          flat.push(item.value);
        }
      }
      const cap = typeof maxItems === 'number' && maxItems > 0 ? maxItems : Infinity;
      const dedupedFlat = Array.from(new Set(flat)).slice(-cap);

      if (isFlatMode) {
        onFlatChange?.(dedupedFlat);
      } else {
        // Apply the same FIFO cap to the split output so both modes behave
        // identically under maxItems.
        const keepSet = new Set(dedupedFlat);
        onUserIdsChange?.(
          Array.from(new Set(nextUserIds)).filter((x) => keepSet.has(x))
        );
        onEmailsChange?.(
          Array.from(new Set(nextEmails)).filter((x) => keepSet.has(x))
        );
      }
    },
    [isFlatMode, onFlatChange, onUserIdsChange, onEmailsChange, allowFreeFormEmails, maxItems]
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    if (option?.kind === 'user') return option.displayName || option.userName || '';
    if (option?.kind === 'placeholder') return option.token || '';
    if (option?.kind === 'email') return option.value || '';
    return '';
  }, []);

  const isOptionEqualToValue = useCallback((option, val) => {
    if (!option || !val) return false;
    if (option.kind === 'user' && val.kind === 'user') return option.userName === val.userName;
    if (option.kind === 'placeholder' && val.kind === 'email') return option.token === val.value;
    if (option.kind === 'email' && val.kind === 'email') return option.value === val.value;
    return false;
  }, []);

  const filterOptions = useCallback((opts, state) => {
    const raw = state.inputValue || '';
    const query = raw.toLowerCase();
    if (!query) return opts;
    // When the user starts with `$`, prefer placeholder matches.
    const startsWithDollar = raw.trim().startsWith('$');
    return opts.filter((opt) => {
      if (opt.kind === 'user') {
        if (startsWithDollar) return false;
        return (
          String(opt.displayName || '').toLowerCase().includes(query) ||
          String(opt.email || '').toLowerCase().includes(query) ||
          String(opt.userName || '').toLowerCase().includes(query)
        );
      }
      if (opt.kind === 'placeholder') {
        // Strip the leading $ or ${ from the query for placeholder matching
        const cleaned = query.replace(/^\$\{?/, '');
        return (
          String(opt.key || '').toLowerCase().includes(cleaned) ||
          String(opt.token || '').toLowerCase().includes(query)
        );
      }
      return false;
    });
  }, []);

  const borderColor = palette?.border || 'divider';
  const primaryText = palette?.textPrimary || 'text.primary';
  const secondaryText = palette?.textSecondary || 'text.secondary';
  const surfaceSubtle = palette?.surfaceSubtle || 'action.hover';

  return (
    <Box
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      sx={{ opacity: disabled ? 0.6 : 1, ...sx }}
    >
      {label ? (
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.label,
            fontWeight: NODE_UI.fontWeight.label,
            color: secondaryText,
            lineHeight: 1.4,
            mb: '4px',
            userSelect: 'none',
          }}
        >
          {label}
        </Typography>
      ) : null}
      <Autocomplete
        multiple
        freeSolo
        size="small"
        disabled={disabled}
        loading={usersLoading}
        options={options}
        value={value}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={filterOptions}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((tagValue, index) => {
            const props = getTagProps({ index });
            if (tagValue?.kind === 'user') {
              return (
                <Chip
                  {...props}
                  key={`user-${tagValue.userName}`}
                  label={tagValue.displayName || tagValue.userName}
                  size="small"
                  avatar={
                    <AgentAvatar
                      name={tagValue.displayName || tagValue.userName || 'User'}
                      size={18}
                      borderRadius="4px"
                    />
                  }
                  deleteIcon={<CloseIcon sx={{ fontSize: '12px !important' }} />}
                  sx={{
                    height: 24,
                    fontSize: 12,
                    fontWeight: 500,
                    backgroundColor: surfaceSubtle,
                    border: '1px solid',
                    borderColor,
                    borderRadius: '6px',
                    color: primaryText,
                    '& .MuiChip-avatar': { ml: '3px' },
                    '& .MuiChip-deleteIcon': {
                      color: secondaryText,
                      opacity: 0.6,
                      '&:hover': { opacity: 1 },
                    },
                  }}
                />
              );
            }
            const emailValue = tagValue?.value || '';
            const isPlaceholder = isPlaceholderToken(emailValue);
            return (
              <Chip
                {...props}
                key={`email-${emailValue}-${index}`}
                label={emailValue}
                size="small"
                icon={
                  isPlaceholder ? (
                    <DataObjectIcon sx={{ fontSize: '12px !important', ml: '4px' }} />
                  ) : undefined
                }
                deleteIcon={<CloseIcon sx={{ fontSize: '12px !important' }} />}
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: isPlaceholder ? 'monospace' : 'inherit',
                  backgroundColor: isPlaceholder ? surfaceSubtle : 'transparent',
                  border: '1px solid',
                  borderColor,
                  borderRadius: '6px',
                  color: primaryText,
                  '& .MuiChip-icon': { color: secondaryText },
                  '& .MuiChip-deleteIcon': {
                    color: secondaryText,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 },
                  },
                }}
              />
            );
          })
        }
        renderOption={(props, option) => {
          if (option.kind === 'placeholder') {
            return (
              <Box
                component="li"
                {...props}
                key={`opt-ph-${option.key}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  px: '10px !important',
                  py: '6px !important',
                }}
              >
                <DataObjectIcon sx={{ fontSize: 16, color: secondaryText }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: NODE_UI.fontSize.value,
                      fontWeight: 500,
                      fontFamily: 'monospace',
                      color: primaryText,
                      lineHeight: 1.3,
                    }}
                  >
                    {option.token}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: NODE_UI.fontSize.caption,
                      color: secondaryText,
                      opacity: 0.7,
                      lineHeight: 1.3,
                    }}
                  >
                    workflow placeholder
                  </Typography>
                </Box>
              </Box>
            );
          }
          return (
            <Box
              component="li"
              {...props}
              key={`opt-user-${option.userName}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                px: '10px !important',
                py: '6px !important',
              }}
            >
              <AgentAvatar
                name={option.displayName || option.userName || 'User'}
                size={22}
                borderRadius="4px"
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: NODE_UI.fontSize.value,
                    fontWeight: 500,
                    color: primaryText,
                    lineHeight: 1.3,
                  }}
                >
                  {option.displayName || option.userName}
                </Typography>
                {option.email ? (
                  <Typography
                    sx={{
                      fontSize: NODE_UI.fontSize.caption,
                      color: secondaryText,
                      opacity: 0.7,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.email}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        }}
        groupBy={(option) => (option.kind === 'user' ? 'Users' : 'Placeholders')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? effectivePlaceholder : ''}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: NODE_UI.fontSize.value,
                backgroundColor: 'background.default',
                borderRadius: NODE_UI.borderRadiusSm,
                padding: '4px 6px !important',
                '& fieldset': { borderColor },
                '&:hover fieldset': { borderColor: secondaryText },
                '&.Mui-focused fieldset': { borderColor: primaryText },
              },
              '& input::placeholder': { color: secondaryText, opacity: 0.5 },
            }}
          />
        )}
      />
      {helperText ? (
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.caption,
            color: secondaryText,
            opacity: 0.55,
            mt: '4px',
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
});

export default NodeEmailRecipientPicker;
